import {
  ArrowLeftIcon,
  PaperAirplaneIcon,
  PaperClipIcon,
  ArrowUturnLeftIcon,
  EllipsisVerticalIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setCommand,
  setMailMessageItemsFilterLoad,
  setMailMessageItemsLoad,
} from "../../redux/mailSlice";
import { getAccesToken, getUserdetails } from "../../Localstorage/localstorage";
import ListChats from "../../Componenten/ListChats";
import { wa } from "./mailWaTheme";
import { avatarInitial, contactAvatarUrl } from "../../utils/mailContactAvatar";
import { AXIOS_DELETE, AXIOS_GET, AXIOS_POST, AXIOS_UPDATE, AXIOS_UPLOAD_DOCUMENT } from "../../server/api/crud";
import { BASE_URL_API, SOCKET_CHANNELS } from "../../constants/constants";
import { IOSOCKETCLIENT, ensureIOSocketConnected } from "../../linksocket/linksocket";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const MailList = ({ setOpen, mobileChatOpen, onExitMobileChat }) => {
  const dispatch = useDispatch();
  const userdetails = getUserdetails();
  const fileInputRef = useRef(null);
  const chatScrollRef = useRef(null);
  const forceScrollAfterSendRef = useRef(false);
  const typingTimerRef = useRef(null);
  const sentTypingStateRef = useRef(false);
  const [composerText, setComposerText] = useState("");
  const [composerMode, setComposerMode] = useState("quick");
  const [selectedAttachments, setSelectedAttachments] = useState([]);
  const [attachmentsByMessage, setAttachmentsByMessage] = useState({});
  const [replyTarget, setReplyTarget] = useState(null);
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [replyMap, setReplyMap] = useState({});
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [messageSearchQuery, setMessageSearchQuery] = useState("");
  const [messageSearchResults, setMessageSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [isOtherTyping, setIsOtherTyping] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const isIPadDevice = () => {
    if (typeof window === "undefined" || typeof navigator === "undefined") return false;
    return /iPad/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  };
  const isTouchTabletViewport = () => {
    if (typeof window === "undefined") return false;
    const coarsePointer = window.matchMedia?.("(pointer: coarse)")?.matches;
    return Boolean(coarsePointer && window.innerWidth <= 1366);
  };
  const shouldUseBottomComposer = () => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 768 || isIPadDevice() || isTouchTabletViewport();
  };
  const [isCompactComposerMode, setIsCompactComposerMode] = useState(() =>
    typeof window !== "undefined" ? shouldUseBottomComposer() : false
  );

  const { mailMessage, mailMessageItemsFilter, mailMessageItems } = useSelector(
    (state) => state.mail
  );

  useEffect(() => {
    const threadId = mailMessage?.id == null ? null : String(mailMessage.id);
    const filterMessageItems = (mailMessageItemsFilter || []).filter(
      (mail) => String(mail?.mailmessage_id) === threadId
    );
    dispatch(setMailMessageItemsLoad(filterMessageItems));
  }, [mailMessage, mailMessageItemsFilter, dispatch]);

  useEffect(() => {
    const onResize = () => setIsCompactComposerMode(shouldUseBottomComposer());
    window.addEventListener("resize", onResize);
    // re-evaluate once after mount (some iPads report values after first paint)
    const t = window.setTimeout(() => onResize(), 60);
    return () => {
      window.removeEventListener("resize", onResize);
      window.clearTimeout(t);
    };
  }, []);

  useEffect(() => {
    if (!mailMessage?.id) {
      setComposerText("");
      setSelectedAttachments([]);
      setReplyTarget(null);
      setEditingMessage(null);
      setPinnedMessages([]);
      setReplyMap({});
      setAttachmentsByMessage({});
      setMessageSearchQuery("");
      setMessageSearchResults([]);
      setSearchError("");
      setIsOtherTyping(false);
    }
  }, [mailMessage?.id]);

  const scrollToBottom = useCallback((behavior = "auto") => {
    const container = chatScrollRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior });
  }, []);

  useEffect(() => {
    scrollToBottom("auto");
    const raf = window.requestAnimationFrame(() => scrollToBottom("auto"));
    const t = window.setTimeout(() => scrollToBottom("auto"), 140);
    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(t);
    };
  }, [mailMessage?.id, mailMessageItems?.length, scrollToBottom]);

  const openModal = (p_type = "insert") => {
    dispatch(setCommand(p_type));
    setOpen(true);
  };

  const escapeHtml = (value) =>
    String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const textFromHtml = (html) =>
    String(html || "")
      .replace(/<br\s*\/?>/gi, " ")
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const canSend = useMemo(
    () => Boolean(
      mailMessage?.id &&
        (editingMessage?.id ? composerText.trim() : composerText.trim() || selectedAttachments.length > 0) &&
        !isSending
    ),
    [mailMessage?.id, editingMessage?.id, composerText, selectedAttachments.length, isSending]
  );

  const handleSelectAttachments = useCallback((event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setSelectedAttachments((current) => [
      ...current,
      ...files.slice(0, Math.max(0, 5 - current.length)).map((file) => ({
        id: `${file.name}-${file.size}-${file.lastModified}`,
        file,
        name: file.name || "Bijlage",
        size: file.size || 0,
        type: file.type || "application/octet-stream",
      })),
    ]);
    event.target.value = "";
  }, []);

  const removeSelectedAttachment = useCallback((id) => {
    setSelectedAttachments((current) => current.filter((item) => item.id !== id));
  }, []);

  const emitTypingState = useCallback((typing) => {
    if (!mailMessage?.id) return;
    if (sentTypingStateRef.current === typing) return;
    if (!ensureIOSocketConnected()) return;
    sentTypingStateRef.current = typing;
    IOSOCKETCLIENT.emit(SOCKET_CHANNELS.MAIL_TYPING, {
      mailmessage_id: mailMessage.id,
      typing,
    });
  }, [mailMessage?.id]);

  const handleComposerTextChange = useCallback((event) => {
    const value = event.target.value;
    setComposerText(value);

    if (typingTimerRef.current) {
      window.clearTimeout(typingTimerRef.current);
    }

    if (value.trim()) {
      emitTypingState(true);
      typingTimerRef.current = window.setTimeout(() => {
        emitTypingState(false);
      }, 1400);
      return;
    }

    emitTypingState(false);
  }, [emitTypingState]);

  useEffect(() => () => {
    if (typingTimerRef.current) {
      window.clearTimeout(typingTimerRef.current);
    }
    emitTypingState(false);
  }, [emitTypingState]);

  useEffect(() => {
    if (!mailMessage?.id) return undefined;

    const handler = (payload) => {
      if (String(payload?.mailmessage_id) !== String(mailMessage.id)) return;
      if (String(payload?.user_id) === String(userdetails?.user_id)) return;
      setIsOtherTyping(Boolean(payload?.typing));
    };

    IOSOCKETCLIENT.on(SOCKET_CHANNELS.MAIL_TYPING, handler);
    return () => IOSOCKETCLIENT.off(SOCKET_CHANNELS.MAIL_TYPING, handler);
  }, [mailMessage?.id, userdetails?.user_id]);

  useEffect(() => {
    if (!isOtherTyping) return undefined;
    const t = window.setTimeout(() => setIsOtherTyping(false), 2800);
    return () => window.clearTimeout(t);
  }, [isOtherTyping]);

  const uploadAttachmentsForMessage = useCallback(async (messageId, attachments) => {
    if (!messageId || !attachments.length) return [];
    const formData = new FormData();
    attachments.forEach((attachment) => {
      formData.append("attachments", attachment.file);
    });
    const response = await AXIOS_UPLOAD_DOCUMENT(
      `mailbox/message/${messageId}/attachments`,
      formData
    );
    return response?.data?.data?.attachments || [];
  }, []);

  const refreshThreadMessages = useCallback(async () => {
    if (!mailMessage?.id) return [];
    const refreshed = await AXIOS_GET(`mailbox/mailmessage/${mailMessage.id}/items`);
    const refreshedItems = refreshed?.data?.data;
    if (Array.isArray(refreshedItems)) {
      dispatch(setMailMessageItemsFilterLoad(refreshedItems));
      dispatch(setMailMessageItemsLoad(refreshedItems));
      return refreshedItems;
    }
    return [];
  }, [dispatch, mailMessage?.id]);

  const sendMobileMessage = useCallback(async () => {
    if (!canSend) return;
    setIsSending(true);
    try {
      emitTypingState(false);
      if (editingMessage?.id) {
        const body = escapeHtml(composerText.trim()).replace(/\n/g, "<br/>");
        const response = await AXIOS_UPDATE("mailbox/message", editingMessage.id, { body });
        if (response?.status === 200 || response?.status === 201) {
          let items = [];
          try {
            items = await refreshThreadMessages();
          } catch (error) {
            items = [];
          }
          if (!items.length) {
            const updatedItems = mailMessageItems.map((item) =>
              String(item?.id) === String(editingMessage.id)
                ? { ...item, body, edited_at: new Date().toISOString() }
                : item
            );
            dispatch(setMailMessageItemsFilterLoad(updatedItems));
            dispatch(setMailMessageItemsLoad(updatedItems));
          }
          setComposerText("");
          setSelectedAttachments([]);
          setEditingMessage(null);
        }
        return;
      }

      const attachments = selectedAttachments;
      const fallbackText = attachments.length
        ? `Bijlage: ${attachments.map((item) => item.name).join(", ")}`
        : "";
      const messageText = composerText.trim() || fallbackText;
      const textHtml = escapeHtml(messageText).replace(/\n/g, "<br/>");

      const newItemMessage = {
        mailmessage_id: mailMessage?.id,
        author_id: userdetails?.user_id,
        author: userdetails?.full_name,
        avatar: userdetails?.avatar,
        body: textHtml,
        reply_to_messageitem_id: replyTarget?.id,
      };
      const optimisticItem = {
        ...newItemMessage,
        id: `local-${Date.now()}`,
        datetime: new Date().toISOString(),
        status: "send",
      };

      const response = await AXIOS_POST("mailbox/message", newItemMessage);
      if (response?.status === 200 || response?.status === 201) {
        const insertedItem = response?.data?.data?.item;
        const messageId = insertedItem?.id;
        let uploadedAttachments = [];

        if (messageId && attachments.length > 0) {
          uploadedAttachments = await uploadAttachmentsForMessage(messageId, attachments);
          setAttachmentsByMessage((current) => ({
            ...current,
            [messageId]: uploadedAttachments,
          }));
        }

        const itemsFromApi = response?.data?.data?.thread_items;
        let items = Array.isArray(itemsFromApi) && itemsFromApi.length > 0 ? itemsFromApi : null;

        if (!items) {
          try {
            items = await refreshThreadMessages();
          } catch (error) {
            items = [];
          }
        }

        if (!items || items.length === 0) {
          items = [...mailMessageItems, insertedItem || optimisticItem];
        }
        dispatch(setMailMessageItemsFilterLoad(items));
        dispatch(setMailMessageItemsLoad(items));
        forceScrollAfterSendRef.current = true;
        scrollToBottom("smooth");
        window.requestAnimationFrame(() => scrollToBottom("smooth"));
        window.setTimeout(() => {
          scrollToBottom("auto");
          forceScrollAfterSendRef.current = false;
        }, 300);
        setComposerText("");
        setSelectedAttachments([]);
        setReplyTarget(null);
        setEditingMessage(null);
      }
    } finally {
      setIsSending(false);
    }
  }, [
    canSend,
    composerText,
    dispatch,
    editingMessage?.id,
    emitTypingState,
    mailMessage?.id,
    mailMessageItems,
    refreshThreadMessages,
    replyTarget?.id,
    scrollToBottom,
    selectedAttachments,
    uploadAttachmentsForMessage,
    userdetails?.avatar,
    userdetails?.full_name,
    userdetails?.user_id,
  ]);

  useEffect(() => {
    if (!mailMessage?.id) return undefined;
    let cancelled = false;

    const loadThreadContext = async () => {
      const [pinsResult, repliesResult] = await Promise.allSettled([
        AXIOS_GET(`mailbox/mailmessage/${mailMessage.id}/pins`),
        AXIOS_GET(`mailbox/mailmessage/${mailMessage.id}/replies`),
      ]);

      if (cancelled) return;

      if (pinsResult.status === "fulfilled") {
        setPinnedMessages(pinsResult.value?.data?.data || []);
      }

      if (repliesResult.status === "fulfilled") {
        const replies = repliesResult.value?.data?.data || [];
        setReplyMap(
          replies.reduce((map, reply) => {
            map[String(reply.messageitem_id)] = reply;
            return map;
          }, {})
        );
      }
    };

    loadThreadContext();
    return () => {
      cancelled = true;
    };
  }, [mailMessage?.id, mailMessageItems?.length]);

  useEffect(() => {
    if (!mailMessage?.id || !mailMessageItems?.length) {
      setAttachmentsByMessage({});
      return undefined;
    }

    let cancelled = false;
    const ids = Array.from(
      new Set(
        mailMessageItems
          .filter((item) => String(item?.mailmessage_id) === String(mailMessage.id))
          .map((item) => item?.id)
          .filter(Boolean)
      )
    );

    const loadAttachments = async () => {
      const results = await Promise.allSettled(
        ids.map((id) => AXIOS_GET(`mailbox/message/${id}/attachments`))
      );
      if (cancelled) return;

      const next = {};
      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          const attachments = result.value?.data?.data || [];
          if (attachments.length) {
            next[String(ids[index])] = attachments;
          }
        }
      });
      setAttachmentsByMessage(next);
    };

    loadAttachments();
    return () => {
      cancelled = true;
    };
  }, [mailMessage?.id, mailMessageItems]);

  useEffect(() => {
    if (!isSearchOpen || !mailMessage?.id) return undefined;
    const query = messageSearchQuery.trim();
    if (!query) {
      setMessageSearchResults([]);
      setSearchError("");
      return undefined;
    }

    let cancelled = false;
    const t = window.setTimeout(async () => {
      setIsSearching(true);
      setSearchError("");
      try {
        const response = await AXIOS_GET(
          `mailbox/mailmessage/${mailMessage.id}/search?query=${encodeURIComponent(query)}&limit=30`
        );
        if (!cancelled) {
          setMessageSearchResults(response?.data?.data || []);
        }
      } catch (error) {
        if (!cancelled) {
          setSearchError("Zoeken mislukt");
          setMessageSearchResults([]);
        }
      } finally {
        if (!cancelled) {
          setIsSearching(false);
        }
      }
    }, 260);

    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [isSearchOpen, mailMessage?.id, messageSearchQuery]);

  const pinnedMessageIds = useMemo(
    () => new Set(pinnedMessages.map((item) => String(item.messageitem_id))),
    [pinnedMessages]
  );

  const searchMatchIds = useMemo(
    () => new Set(messageSearchResults.map((item) => String(item.id))),
    [messageSearchResults]
  );

  const handleTogglePin = useCallback(async (message) => {
    if (!mailMessage?.id || !message?.id) return;
    const alreadyPinned = pinnedMessageIds.has(String(message.id));
    if (alreadyPinned) {
      await AXIOS_DELETE(`mailbox/mailmessage/${mailMessage.id}/pins`, message.id);
      setPinnedMessages((current) =>
        current.filter((item) => String(item.messageitem_id) !== String(message.id))
      );
      return;
    }

    await AXIOS_POST(`mailbox/mailmessage/${mailMessage.id}/pins/${message.id}`, {});
    setPinnedMessages((current) => [
      {
        mailmessage_id: mailMessage.id,
        messageitem_id: message.id,
        author: message.author,
        author_id: message.author_id,
        body: message.body,
        datetime: message.datetime,
      },
      ...current.filter((item) => String(item.messageitem_id) !== String(message.id)),
    ]);
  }, [mailMessage?.id, pinnedMessageIds]);

  const handleReplyToMessage = useCallback((message) => {
    setReplyTarget(message);
    setEditingMessage(null);
    setComposerMode("quick");
  }, []);

  const handleEditMessage = useCallback((message) => {
    setEditingMessage(message);
    setReplyTarget(null);
    setSelectedAttachments([]);
    setComposerText(textFromHtml(message?.body));
    setComposerMode("quick");
  }, []);

  const handleDeleteMessage = useCallback(async (message) => {
    if (!message?.id) return;
    await AXIOS_DELETE("mailbox/message", message.id);
    const updatedItems = mailMessageItems.map((item) =>
      String(item?.id) === String(message.id)
        ? { ...item, body: "", deleted_at: new Date().toISOString() }
        : item
    );
    dispatch(setMailMessageItemsFilterLoad(updatedItems));
    dispatch(setMailMessageItemsLoad(updatedItems));
  }, [dispatch, mailMessageItems]);

  const handleDownloadAttachment = useCallback(async (messageId, attachment) => {
    if (!messageId || !attachment?.id) return;
    const response = await fetch(
      `${BASE_URL_API}/mailbox/message/${messageId}/attachments/${attachment.id}/download`,
      {
        headers: {
          Authorization: `Bearer ${getAccesToken() || ""}`,
        },
      }
    );
    if (!response.ok) return;
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = attachment.original_name || "bijlage";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }, []);

  const composerModeToggle = (
    <div className="inline-flex h-9 shrink-0 rounded-full border border-wa-border bg-wa-panel p-0.5 text-xs font-medium text-wa-muted">
      <button
        type="button"
        onClick={() => setComposerMode("quick")}
        className={classNames(
          "rounded-full px-3 transition-colors",
          composerMode === "quick" ? "bg-wa-accent text-wa-onaccent" : "hover:text-wa-text"
        )}
      >
        Chat
      </button>
      <button
        type="button"
        onClick={() => setComposerMode("rich")}
        className={classNames(
          "rounded-full px-3 transition-colors",
          composerMode === "rich" ? "bg-wa-accent text-wa-onaccent" : "hover:text-wa-text"
        )}
      >
        Rich
      </button>
    </div>
  );

  const subtitle =
    userdetails.role_id === 1
      ? mailMessage?.sender || "Geen patient"
      : mailMessage?.receiver || "Geen specialist";

  const title = mailMessage?.subject || "Berichten";

  const headerAvatarUrl = useMemo(
    () => contactAvatarUrl(mailMessage, userdetails?.user_id),
    [mailMessage, userdetails?.user_id]
  );

  return (
    <section
      aria-labelledby="message-heading"
      className={classNames(
        "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden border-l",
        wa.border,
        wa.app,
        mobileChatOpen ? "flex" : "hidden md:flex"
      )}
    >
      <header
        className={classNames(
          "flex flex-shrink-0 items-center gap-2 border-b px-2 py-2 sm:px-3",
          wa.border,
          wa.panel2
        )}
      >
        <button
          type="button"
          onClick={onExitMobileChat}
          className={classNames(
            "rounded-full p-2 md:hidden",
            wa.muted,
            wa.hover,
            "hover:text-wa-text"
          )}
          aria-label="Terug naar gesprekken"
        >
          <ArrowLeftIcon className="h-6 w-6" />
        </button>
        <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
          <div className="relative h-10 w-10 flex-shrink-0 overflow-hidden rounded-full border border-wa-border bg-gradient-to-br from-wa-panel2 to-wa-panel">
            {headerAvatarUrl ? (
              <img
                src={headerAvatarUrl}
                alt=""
                className="h-full w-full object-cover"
                loading="lazy"
                decoding="async"
              />
            ) : (
              <div
                className={classNames(
                  "flex h-full w-full items-center justify-center text-sm font-semibold",
                  wa.text
                )}
              >
                {avatarInitial(subtitle)}
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h1 id="message-heading" className={classNames("truncate text-[15px] font-semibold sm:text-base", wa.text)}>
              {title}
            </h1>
            <p className={classNames("truncate text-xs", isOtherTyping ? wa.accent : wa.muted)}>
              {isOtherTyping ? "typing..." : `Contact: ${subtitle}`}
            </p>
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-0.5">
          {mailMessage ? <div className="mr-1 hidden md:block">{composerModeToggle}</div> : null}
          {mailMessage?.category ? (
            <span
              className={classNames(
                "mr-1 hidden max-w-[8rem] truncate rounded-full px-2 py-0.5 text-xs font-medium sm:inline",
                wa.panel,
                wa.muted
              )}
            >
              {mailMessage.category}
            </span>
          ) : null}
          <button
            type="button"
            onClick={() => setIsSearchOpen((open) => !open)}
            className={classNames(
              "hidden rounded-full p-2 sm:inline-flex",
              isSearchOpen ? wa.accent : wa.muted,
              wa.hover,
              "hover:text-wa-text"
            )}
            aria-label="Zoeken in gesprek"
          >
            <MagnifyingGlassIcon className="h-5 w-5" />
          </button>
          {mailMessage && !isCompactComposerMode ? (
            <button
              type="button"
              onClick={() => openModal("update")}
              title="Beantwoorden"
              className={classNames(
                "rounded-full p-2 transition-colors",
                wa.muted,
                wa.hover,
                "hover:text-wa-text"
              )}
            >
              <ArrowUturnLeftIcon className="h-5 w-5" />
            </button>
          ) : null}
          <button
            type="button"
            className={classNames(
              "rounded-full p-2",
              wa.muted,
              wa.hover,
              "hover:text-wa-text"
            )}
            aria-label="Meer"
          >
            <EllipsisVerticalIcon className="h-5 w-5" />
          </button>
        </div>
      </header>

      {mailMessage && isSearchOpen ? (
        <div className={classNames("border-b px-3 py-2", wa.border, wa.panel)}>
          <div className="flex items-center gap-2">
            <MagnifyingGlassIcon className={classNames("h-5 w-5 flex-shrink-0", wa.muted)} />
            <input
              value={messageSearchQuery}
              onChange={(event) => setMessageSearchQuery(event.target.value)}
              placeholder="Zoeken in gesprek"
              className="h-9 min-w-0 flex-1 rounded-full border border-wa-border bg-wa-panel2 px-3 text-sm text-wa-text outline-none placeholder:text-wa-muted focus:border-wa-accent"
            />
            <button
              type="button"
              onClick={() => {
                setIsSearchOpen(false);
                setMessageSearchQuery("");
                setMessageSearchResults([]);
                setSearchError("");
              }}
              className={classNames("rounded-full p-2", wa.muted, wa.hover)}
              aria-label="Zoeken sluiten"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          {messageSearchQuery.trim() ? (
            <p className={classNames("mt-1 px-8 text-xs", searchError ? "text-red-400" : wa.muted)}>
              {searchError || (isSearching ? "Zoeken..." : `${messageSearchResults.length} resultaten`)}
            </p>
          ) : null}
        </div>
      ) : null}

      {mailMessage && pinnedMessages.length > 0 ? (
        <div className={classNames("border-b px-3 py-2", wa.border, wa.panel2)}>
          <div className="flex min-w-0 items-center gap-2 overflow-x-auto">
            <span className={classNames("shrink-0 text-xs font-semibold uppercase", wa.accent)}>
              Vastgemaakt
            </span>
            {pinnedMessages.slice(0, 3).map((pin) => (
              <button
                key={pin.messageitem_id}
                type="button"
                onClick={() => {
                  const node = document.getElementById(`mail-message-${pin.messageitem_id}`);
                  node?.scrollIntoView({ behavior: "smooth", block: "center" });
                }}
                className="max-w-[16rem] shrink-0 truncate rounded-full border border-wa-border bg-wa-panel px-3 py-1 text-left text-xs text-wa-text hover:bg-wa-hover"
                title={textFromHtml(pin.body)}
              >
                {pin.author ? `${pin.author}: ` : ""}
                {textFromHtml(pin.body) || "Bericht"}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {!mailMessage ? (
        <div
          className={classNames(
            "flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center",
            wa.muted
          )}
        >
          <div className={classNames("rounded-full p-6", wa.panel)}>
            <ArrowUturnLeftIcon className="h-10 w-10 opacity-40" aria-hidden />
          </div>
          <p className="max-w-sm text-sm">
            Selecteer een gesprek in de lijst om berichten te bekijken, of start een nieuw gesprek.
          </p>
        </div>
      ) : (
        <div
          className="relative min-h-0 flex-1 overflow-hidden bg-wa-app"
          style={{
            backgroundImage:
              "radial-gradient(circle, rgb(var(--wa-border) / 0.35) 1.05px, transparent 1.05px)",
            backgroundSize: "44px 44px",
          }}
        >
          <div
            ref={chatScrollRef}
            className="h-full min-h-0 overflow-y-auto overscroll-contain"
            onLoadCapture={(event) => {
              if (!forceScrollAfterSendRef.current) return;
              if (event?.target?.tagName === "IMG") {
                scrollToBottom("auto");
              }
            }}
          >
            <ListChats
              chats={mailMessageItems}
              user={userdetails}
              attachmentsByMessage={attachmentsByMessage}
              replyMap={replyMap}
              pinnedMessageIds={pinnedMessageIds}
              searchMatchIds={searchMatchIds}
              onReply={handleReplyToMessage}
              onTogglePin={handleTogglePin}
              onEdit={handleEditMessage}
              onDelete={handleDeleteMessage}
              onDownloadAttachment={handleDownloadAttachment}
            />
          </div>
        </div>
      )}
      {mailMessage ? (
        <footer className={classNames("border-t px-2 py-2 shadow-[0_-8px_20px_rgba(0,0,0,0.2)]", wa.border, wa.panel2)}>
          <div className="mb-2 flex items-center justify-between gap-2 md:hidden">
            {composerModeToggle}
          </div>
          {composerMode === "rich" ? (
            <div className="flex items-center justify-between gap-3 rounded-2xl border border-wa-border bg-wa-panel px-3 py-2">
              <span className={classNames("truncate text-sm", wa.text)}>
                Rich editor
              </span>
              <button
                type="button"
                onClick={() => openModal("update")}
                className="shrink-0 rounded-full bg-wa-accent px-4 py-2 text-sm font-semibold text-wa-onaccent transition hover:bg-wa-accentdeep"
              >
                Open
              </button>
            </div>
          ) : (
            <>
          {replyTarget || editingMessage ? (
            <div className="mb-2 flex items-center gap-2 rounded-lg border border-wa-border bg-wa-panel p-2">
              <div className={classNames("min-w-0 flex-1 border-l-2 border-wa-accent pl-2 text-xs", wa.muted)}>
                <p className={classNames("font-semibold", wa.text)}>
                  {editingMessage ? "Bewerken" : replyTarget?.author || "Antwoord"}
                </p>
                <p className="truncate">
                  {textFromHtml(editingMessage?.body || replyTarget?.body) || "Bericht"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setReplyTarget(null);
                  setEditingMessage(null);
                  setComposerText("");
                }}
                className={classNames("rounded-full p-1.5", wa.muted, wa.hover)}
                aria-label="Annuleren"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          ) : null}
          {selectedAttachments.length > 0 ? (
            <div className="mb-2 flex flex-wrap gap-2">
              {selectedAttachments.map((attachment) => (
                <div
                  key={attachment.id}
                  className="flex min-w-0 max-w-[15rem] items-center gap-2 rounded-full border border-wa-border bg-wa-panel px-3 py-1.5 text-xs text-wa-text"
                >
                  <PaperClipIcon className="h-4 w-4 flex-shrink-0 text-wa-accent" />
                  <span className="min-w-0 truncate">{attachment.name}</span>
                  <button
                    type="button"
                    onClick={() => removeSelectedAttachment(attachment.id)}
                    className={classNames("rounded-full p-0.5", wa.muted, wa.hover)}
                    aria-label="Bijlage verwijderen"
                  >
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          ) : null}
          <div className="flex items-end gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={Boolean(editingMessage)}
              className={classNames(
                "inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full",
                wa.muted,
                wa.hover,
                "hover:text-wa-text disabled:cursor-not-allowed disabled:opacity-40"
              )}
              aria-label="Bijlage toevoegen"
            >
              <PaperClipIcon className="h-5 w-5" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
              className="hidden"
              onChange={handleSelectAttachments}
            />
            <textarea
              rows={1}
              value={composerText}
              onChange={handleComposerTextChange}
              placeholder={editingMessage ? "Bericht bewerken..." : "Typ een bericht..."}
              className="max-h-28 min-h-[40px] flex-1 resize-none rounded-2xl border border-wa-border bg-wa-panel px-3 py-2 text-sm text-wa-text outline-none placeholder:text-wa-muted focus:border-wa-accent"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMobileMessage();
                }
              }}
            />
            <button
              type="button"
              onClick={sendMobileMessage}
              disabled={!canSend}
              className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-wa-accent text-wa-onaccent transition disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Versturen"
            >
              <PaperAirplaneIcon className="h-5 w-5" />
            </button>
          </div>
            </>
          )}
        </footer>
      ) : null}
    </section>
  );
};

export default MailList;
